import React, { useRef, useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';

const LiquidGlassButton = ({
  children = "Button",
  onClick,
  variant = "primary",
  size = "medium",
  disabled = false,
  className = "",
  style = {},
  ...props
}) => {
  const buttonRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isPressed, setIsPressed] = useState(false);
  
  // Motion values for parallax effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Transform mouse position to rotation and translation
  const rotateX = useTransform(mouseY, [-100, 100], [15, -15]);
  const rotateY = useTransform(mouseX, [-100, 100], [-15, 15]);
  const translateZ = useTransform(mouseX, [-100, 100], [-10, 10]);
  
  // Spring animations for smooth transitions
  const springConfig = { damping: 15, stiffness: 150 };
  const springRotateX = useSpring(rotateX, springConfig);
  const springRotateY = useSpring(rotateY, springConfig);
  const springTranslateZ = useSpring(translateZ, springConfig);

  // Handle mouse movement for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!buttonRef.current) return;
      
      const rect = buttonRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const x = e.clientX - centerX;
      const y = e.clientY - centerY;
      
      mouseX.set(x);
      mouseY.set(y);
      setMousePosition({ x, y });
    };

    const handleMouseLeave = () => {
      mouseX.set(0);
      mouseY.set(0);
      setMousePosition({ x: 0, y: 0 });
    };

    const button = buttonRef.current;
    if (button) {
      button.addEventListener('mousemove', handleMouseMove);
      button.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (button) {
        button.removeEventListener('mousemove', handleMouseMove);
        button.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [mouseX, mouseY]);

  // Variant styles
  const variants = {
    primary: {
      background: `
        linear-gradient(135deg, 
          rgba(255, 255, 255, 0.1) 0%, 
          rgba(255, 255, 255, 0.05) 50%, 
          rgba(255, 255, 255, 0.1) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(255, 255, 255, 0.2) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 255, 255, 0.3)',
      color: 'rgba(255, 255, 255, 0.9)',
      shadowColor: 'rgba(0, 123, 255, 0.3)'
    },
    secondary: {
      background: `
        linear-gradient(135deg, 
          rgba(255, 255, 255, 0.08) 0%, 
          rgba(255, 255, 255, 0.03) 50%, 
          rgba(255, 255, 255, 0.08) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(255, 255, 255, 0.15) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      color: 'rgba(255, 255, 255, 0.8)',
      shadowColor: 'rgba(255, 255, 255, 0.2)'
    },
    success: {
      background: `
        linear-gradient(135deg, 
          rgba(40, 167, 69, 0.1) 0%, 
          rgba(40, 167, 69, 0.05) 50%, 
          rgba(40, 167, 69, 0.1) 100%
        ),
        radial-gradient(
          circle at 30% 30%, 
          rgba(40, 167, 69, 0.2) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(40, 167, 69, 0.3)',
      color: 'rgba(255, 255, 255, 0.9)',
      shadowColor: 'rgba(40, 167, 69, 0.3)'
    },
    danger: {
      background: `
        linear-gradient(135deg, 
          rgba(220, 53, 69, 0.1) 0%, 
          rgba(220, 53, 69, 0.05) 50%, 
          rgba(220, 53, 69, 0.1) 100%
        ),
        radial-gradient(
          circle at 70% 70%, 
          rgba(220, 53, 69, 0.2) 0%, 
          transparent 50%
        )
      `,
      borderColor: 'rgba(220, 53, 69, 0.3)',
      color: 'rgba(255, 255, 255, 0.9)',
      shadowColor: 'rgba(220, 53, 69, 0.3)'
    }
  };

  // Size styles
  const sizes = {
    small: {
      padding: '8px 16px',
      fontSize: '14px',
      borderRadius: '8px',
      minHeight: '32px'
    },
    medium: {
      padding: '12px 24px',
      fontSize: '16px',
      borderRadius: '12px',
      minHeight: '44px'
    },
    large: {
      padding: '16px 32px',
      fontSize: '18px',
      borderRadius: '16px',
      minHeight: '56px'
    }
  };

  const currentVariant = variants[variant] || variants.primary;
  const currentSize = sizes[size] || sizes.medium;

  return (
    <motion.button
      ref={buttonRef}
      onClick={onClick}
      disabled={disabled}
      className={`liquid-glass-button ${className}`}
      style={{
        position: 'relative',
        border: `1px solid ${currentVariant.borderColor}`,
        borderRadius: currentSize.borderRadius,
        padding: currentSize.padding,
        fontSize: currentSize.fontSize,
        minHeight: currentSize.minHeight,
        color: currentVariant.color,
        background: currentVariant.background,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        backgroundBlendMode: 'overlay',
        mixBlendMode: 'normal',
        cursor: disabled ? 'not-allowed' : 'pointer',
        outline: 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transformStyle: 'preserve-3d',
        perspective: '1000px',
        ...style
      }}
      whileHover={{
        scale: disabled ? 1 : 1.02,
        boxShadow: disabled ? 'none' : `
          0 20px 40px rgba(0, 0, 0, 0.3),
          0 0 20px ${currentVariant.shadowColor},
          inset 0 1px 0 rgba(255, 255, 255, 0.2)
        `,
        filter: disabled ? 'none' : 'brightness(1.1)',
        transition: { duration: 0.2 }
      }}
      whileTap={{
        scale: disabled ? 1 : 0.98,
        boxShadow: disabled ? 'none' : `
          0 10px 20px rgba(0, 0, 0, 0.4),
          inset 0 2px 4px rgba(0, 0, 0, 0.1)
        `,
        transition: { duration: 0.1 }
      }}
      animate={{
        rotateX: disabled ? 0 : springRotateX,
        rotateY: disabled ? 0 : springRotateY,
        translateZ: disabled ? 0 : springTranslateZ,
      }}
      onMouseDown={() => !disabled && setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      {...props}
    >
      {/* Glass overlay effect */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              135deg,
              rgba(255, 255, 255, 0.1) 0%,
              transparent 50%,
              rgba(255, 255, 255, 0.05) 100%
            )
          `,
          borderRadius: 'inherit',
          pointerEvents: 'none',
          mixBlendMode: 'overlay'
        }}
        animate={{
          opacity: isPressed ? 0.3 : 0.6,
          scale: isPressed ? 0.95 : 1
        }}
        transition={{ duration: 0.2 }}
      />
      
      {/* Inner glow effect */}
      <motion.div
        style={{
          position: 'absolute',
          top: '2px',
          left: '2px',
          right: '2px',
          bottom: '2px',
          background: `
            radial-gradient(
              circle at 50% 0%,
              rgba(255, 255, 255, 0.1) 0%,
              transparent 70%
            )
          `,
          borderRadius: 'inherit',
          pointerEvents: 'none',
          filter: 'blur(1px)'
        }}
        animate={{
          opacity: isPressed ? 0.1 : 0.3
        }}
        transition={{ duration: 0.2 }}
      />
      
      {/* Content with proper z-index */}
      <motion.span
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontWeight: 500,
          letterSpacing: '0.5px',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
        }}
        animate={{
          y: isPressed ? 1 : 0
        }}
        transition={{ duration: 0.1 }}
      >
        {children}
      </motion.span>
      
      {/* Ripple effect on click */}
      <motion.div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 0,
          height: 0,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.3)',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none'
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{
          scale: isPressed ? 2 : 0,
          opacity: isPressed ? 0 : 0
        }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      />
    </motion.button>
  );
};

export default LiquidGlassButton; 